/**
 * RequestsTab Component
 *
 * Table-based view for managing HMC ticket requests with sortable columns.
 * Details are shown in a modal (RequestDetailsModal).
 *
 * Created: 2026-01-13
 * Updated: 2026-01-14 - Simplified to single-panel layout, modal-only details
 * Updated: 2026-01-14 - Added sortable column headers and editable date/time received
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTicketRequestsStore, type TicketRequest } from '../../stores/ticketRequestsStore';
import { RequestsToolbar } from './RequestsToolbar';
import { RequestDetailsModal } from './RequestDetailsModal';
import { format, parseISO } from 'date-fns';
import {
  Loader2,
  Inbox,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Clock4,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Archive,
  Eye,
} from 'lucide-react';

interface RequestsTabProps {
  onBackToEvents?: () => void;
}

// Sort direction type
type SortDirection = 'asc' | 'desc' | null;

// Sortable column definition
type SortableColumn = 'full_name' | 'company' | 'event_name' | 'created_at' | 'ticket_count' | 'status';

// Status badge colors
const STATUS_COLORS: Record<TicketRequest['status'], { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Approved' },
  denied: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Denied' },
  waitlist: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Waitlist' },
  fulfilled: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Fulfilled' },
};

// Status icons
const STATUS_ICONS: Record<TicketRequest['status'], React.ReactNode> = {
  pending: <Clock4 className="h-3 w-3" />,
  approved: <CheckCircle className="h-3 w-3" />,
  denied: <XCircle className="h-3 w-3" />,
  waitlist: <Clock className="h-3 w-3" />,
  fulfilled: <Star className="h-3 w-3" />,
};

export const RequestsTab: React.FC<RequestsTabProps> = ({ onBackToEvents }) => {
  const {
    requests,
    total,
    stats,
    isLoading,
    isUpdating,
    error,
    filters,
    fetchRequests,
    fetchStats,
    fetchAllEventAvailability,
    updateReceivedAt,
    updateRequestStatus,
    clearError,
  } = useTicketRequestsStore();

  // Modal state for request details
  const [modalRequest, setModalRequest] = useState<TicketRequest | null>(null);
  const [modalRequestIndex, setModalRequestIndex] = useState<number>(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Editing state for date/time
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [editingDate, setEditingDate] = useState<string>('');
  const [editingTime, setEditingTime] = useState<string>('');

  // Editing state for status
  const [editingStatusRequestId, setEditingStatusRequestId] = useState<string | null>(null);

  // Initial data fetch
  useEffect(() => {
    fetchRequests();
    fetchStats();
    fetchAllEventAvailability();
  }, [fetchRequests, fetchStats, fetchAllEventAvailability]);

  // Keep modalRequest in sync with store updates
  useEffect(() => {
    if (modalRequest && isModalOpen) {
      const updatedRequest = requests.find(r => r.request_id === modalRequest.request_id);
      if (updatedRequest && updatedRequest !== modalRequest) {
        setModalRequest(updatedRequest);
      }
    }
  }, [requests, modalRequest, isModalOpen]);

  // Handle column header click for sorting
  const handleSort = useCallback((column: SortableColumn) => {
    if (sortColumn === column) {
      // Toggle direction: null -> asc -> desc -> null
      if (sortDirection === null) {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  // Sort requests
  const sortedRequests = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return requests;
    }

    return [...requests].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortColumn) {
        case 'full_name':
          aVal = a.full_name.toLowerCase();
          bVal = b.full_name.toLowerCase();
          break;
        case 'company':
          aVal = a.company.toLowerCase();
          bVal = b.company.toLowerCase();
          break;
        case 'event_name':
          aVal = a.event_name.toLowerCase();
          bVal = b.event_name.toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at;
          bVal = b.created_at;
          break;
        case 'ticket_count':
          aVal = a.ticket_count;
          bVal = b.ticket_count;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [requests, sortColumn, sortDirection]);

  // Handle view details modal
  const handleViewDetails = useCallback((request: TicketRequest) => {
    const index = sortedRequests.findIndex((r) => r.request_id === request.request_id);
    setModalRequest(request);
    setModalRequestIndex(index);
    setIsModalOpen(true);
  }, [sortedRequests]);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => {
      setModalRequest(null);
      setModalRequestIndex(-1);
    }, 200);
  }, []);

  // Navigate to previous request
  const handlePreviousRequest = useCallback(() => {
    if (modalRequestIndex > 0) {
      const prevRequest = sortedRequests[modalRequestIndex - 1];
      setModalRequest(prevRequest);
      setModalRequestIndex(modalRequestIndex - 1);
    }
  }, [modalRequestIndex, sortedRequests]);

  // Navigate to next request
  const handleNextRequest = useCallback(() => {
    if (modalRequestIndex < sortedRequests.length - 1) {
      const nextRequest = sortedRequests[modalRequestIndex + 1];
      setModalRequest(nextRequest);
      setModalRequestIndex(modalRequestIndex + 1);
    }
  }, [modalRequestIndex, sortedRequests]);

  // Start editing date/time
  const handleStartEdit = useCallback((request: TicketRequest, e: React.MouseEvent) => {
    e.stopPropagation();
    const date = request.created_at ? parseISO(request.created_at) : new Date();
    setEditingRequestId(request.request_id);
    setEditingDate(format(date, 'yyyy-MM-dd'));
    setEditingTime(format(date, 'HH:mm'));
  }, []);

  // Save edited date/time
  const handleSaveDateTime = useCallback(async (requestId: string) => {
    if (!editingDate || !editingTime) return;

    const newDateTime = `${editingDate}T${editingTime}:00`;
    const success = await updateReceivedAt(requestId, newDateTime);

    if (success) {
      setEditingRequestId(null);
      setEditingDate('');
      setEditingTime('');
    }
  }, [editingDate, editingTime, updateReceivedAt]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingRequestId(null);
    setEditingDate('');
    setEditingTime('');
  }, []);

  // Handle status change from dropdown
  const handleStatusChange = useCallback(async (requestId: string, newStatus: string) => {
    const success = await updateRequestStatus(requestId, newStatus);
    if (success) {
      setEditingStatusRequestId(null);
    }
  }, [updateRequestStatus]);

  // Render sort icon
  const renderSortIcon = (column: SortableColumn) => {
    if (sortColumn !== column) {
      return <div className="w-4 h-4" />; // Placeholder for alignment
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className="w-4 h-4" />;
    }
    if (sortDirection === 'desc') {
      return <ChevronDown className="w-4 h-4" />;
    }
    return <div className="w-4 h-4" />;
  };

  // Render loading state
  if (isLoading && requests.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          <p className="text-dark-muted">Loading requests...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && requests.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => {
              clearError();
              fetchRequests();
            }}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (requests.length === 0 && !isLoading) {
    return (
      <div className="flex h-full flex-col">
        <RequestsToolbar stats={stats} />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <Inbox className="h-12 w-12 text-dark-muted" />
            <h3 className="text-lg font-medium text-dark-text">No ticket requests</h3>
            <p className="text-sm text-dark-muted">
              {filters.status || filters.event_id || filters.search
                ? 'No requests match your filters. Try clearing them.'
                : 'Ticket requests will appear here when guests submit them.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="requests-tab" className="flex h-full flex-col">
      {/* Back to Events header */}
      {onBackToEvents && (
        <div className="flex items-center gap-3 border-b border-dark-border/30 bg-dark-surface/50 px-4 py-3">
          <button
            onClick={onBackToEvents}
            data-testid="back-to-events-btn"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-dark-muted hover:bg-dark-input hover:text-white transition-all duration-150 min-h-[44px]"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Back to Events</span>
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">Ticket Request Tracking</h2>
          </div>
        </div>
      )}

      {/* Toolbar with filters and stats */}
      <RequestsToolbar stats={stats} />

      {/* Table container */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Table header with sortable columns */}
        <div className="border-b border-dark-border/30 bg-dark-surface/70">
          <div className="grid grid-cols-[1fr_1fr_0.8fr_220px_60px_90px_70px] gap-3 px-4 py-3 text-xs font-medium text-dark-muted uppercase tracking-wider">
            <button
              onClick={() => handleSort('full_name')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-name"
            >
              <span>Name</span>
              {renderSortIcon('full_name')}
            </button>
            <button
              onClick={() => handleSort('company')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-company"
            >
              <span>Company</span>
              {renderSortIcon('company')}
            </button>
            <button
              onClick={() => handleSort('event_name')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-event"
            >
              <span>Event</span>
              {renderSortIcon('event_name')}
            </button>
            <button
              onClick={() => handleSort('created_at')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-received"
            >
              <span>Date/Time Received</span>
              {renderSortIcon('created_at')}
            </button>
            <button
              onClick={() => handleSort('ticket_count')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-tickets"
            >
              <span>Tickets</span>
              {renderSortIcon('ticket_count')}
            </button>
            <button
              onClick={() => handleSort('status')}
              className="flex items-center gap-1 hover:text-white transition-all duration-150 text-left"
              data-testid="sort-status"
            >
              <span>Status</span>
              {renderSortIcon('status')}
            </button>
            <span>Actions</span>
          </div>
        </div>

        {/* Count indicator */}
        <div className="flex items-center justify-between border-b border-dark-border/30/50 px-4 py-2 bg-dark-surface/30">
          <span className="text-sm text-dark-muted">
            {total} request{total !== 1 ? 's' : ''}
            {filters.status && ` · ${filters.status}`}
          </span>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-purple-500" />}
        </div>

        {/* Scrollable table body */}
        <div data-testid="requests-list" className="flex-1 overflow-y-auto">
          {sortedRequests.map((request) => {
            const statusConfig = STATUS_COLORS[request.status];
            const statusIcon = STATUS_ICONS[request.status];
            const isEditing = editingRequestId === request.request_id;

            return (
              <div
                key={request.request_id}
                onClick={() => handleViewDetails(request)}
                data-testid={`request-row-${request.request_id}`}
                className="grid grid-cols-[1fr_1fr_0.8fr_220px_60px_90px_70px] gap-3 px-4 py-3 border-b border-dark-border/30/30 hover:bg-dark-surface/50 cursor-pointer items-center"
              >
                {/* Name */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-white truncate">{request.full_name}</span>
                  {request.is_standby_request && (
                    <span className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-xs text-yellow-400 flex-shrink-0">
                      Standby
                    </span>
                  )}
                  {request.is_archived && (
                    <span className="flex items-center gap-1 rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-400 flex-shrink-0">
                      <Archive className="h-3 w-3" />
                    </span>
                  )}
                </div>

                {/* Company */}
                <div className="text-dark-text truncate">{request.company}</div>

                {/* Event */}
                <div className="text-dark-text truncate">{request.event_name}</div>

                {/* Date/Time Received - Editable */}
                <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                  {isEditing ? (
                    <div className="flex items-center gap-0.5">
                      <input
                        type="date"
                        value={editingDate}
                        onChange={(e) => setEditingDate(e.target.value)}
                        className="px-1 py-0.5 text-[11px] rounded bg-dark-input border border-dark-border/30 text-white w-[105px]"
                        data-testid={`edit-date-${request.request_id}`}
                      />
                      <input
                        type="time"
                        value={editingTime}
                        onChange={(e) => setEditingTime(e.target.value)}
                        className="px-1 py-0.5 text-[11px] rounded bg-dark-input border border-dark-border/30 text-white w-[70px]"
                        data-testid={`edit-time-${request.request_id}`}
                      />
                      <button
                        onClick={() => handleSaveDateTime(request.request_id)}
                        disabled={isUpdating}
                        className="p-0.5 text-green-400 hover:text-green-300 disabled:opacity-50"
                        title="Save"
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-0.5 text-dark-muted hover:text-dark-text"
                        title="Cancel"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => handleStartEdit(request, e)}
                      className="text-xs text-dark-muted hover:text-white hover:bg-dark-input/50 px-1.5 py-0.5 rounded transition-all duration-150 text-left"
                      data-testid={`datetime-btn-${request.request_id}`}
                      title="Click to edit"
                    >
                      {request.created_at
                        ? format(parseISO(request.created_at), 'MMM d, yyyy h:mm a')
                        : '-'}
                    </button>
                  )}
                </div>

                {/* Tickets */}
                <div className="text-purple-400 font-medium">{request.ticket_count}</div>

                {/* Status - Editable Dropdown */}
                <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                  {editingStatusRequestId === request.request_id ? (
                    <select
                      value={request.status}
                      onChange={(e) => handleStatusChange(request.request_id, e.target.value)}
                      onBlur={() => setEditingStatusRequestId(null)}
                      autoFocus
                      disabled={isUpdating}
                      className="px-2 py-1 text-xs rounded-lg bg-dark-input border border-dark-border/30 text-white focus:border-purple-500 focus:outline-none cursor-pointer"
                      data-testid={`status-select-${request.request_id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="denied">Denied</option>
                      <option value="waitlist">Waitlist</option>
                      <option value="fulfilled">Fulfilled</option>
                    </select>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingStatusRequestId(request.request_id);
                      }}
                      className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium w-fit ${statusConfig.bg} ${statusConfig.text} hover:ring-2 hover:ring-purple-500/50 transition-all cursor-pointer`}
                      title="Click to change status"
                      data-testid={`status-btn-${request.request_id}`}
                    >
                      {statusIcon}
                      <span>{statusConfig.label}</span>
                    </button>
                  )}
                </div>

                {/* Actions */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(request);
                  }}
                  data-testid={`view-details-btn-${request.request_id}`}
                  className="flex items-center gap-1 rounded-lg bg-dark-input px-2 py-1 text-xs font-medium text-dark-text hover:bg-dark-surface/40 hover:text-white transition-all duration-150"
                >
                  <Eye className="h-3 w-3" />
                  View
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Request Details Modal */}
      {modalRequest && (
        <RequestDetailsModal
          request={modalRequest}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          currentIndex={modalRequestIndex}
          totalRequests={sortedRequests.length}
          onPrevious={handlePreviousRequest}
          onNext={handleNextRequest}
        />
      )}
    </div>
  );
};
